import { z } from 'zod';
import { Nullable, SelectOption } from 'global';
import { UseFormReturn } from 'react-hook-form';
import { ListingForm } from 'components/shared/TableListing/types';
import { BasePrice, Charge, ChargeBucketRef } from 'modules/equipment/types';
import { QuoteCalcLineItemType as QuoteLineItemType } from '@trnsact/trnsact-shared-types';

import type { ChargeListingService } from '../lib';
import type { QuoteCalcViewModel } from '../api/types';
import { quoteSchema, formQuoteSchema } from '../model/schema';

export type Quote = z.infer<typeof quoteSchema>;
export type QuoteFormValues = z.infer<typeof formQuoteSchema>;

export { QuoteLineItemType };

export interface QuoteArrayFieldsProps {
  equipmentId: Nullable<string>;
}

export type CreateQuoteFormValues = {
  notesToBuyer: string;
  notification: {
    transport: string;
    emails: SelectOption[];
    ccEmails: SelectOption[];
  };
};

export type QuoteEquipmentAttachmentWithCharge = Record<string, any> & { price: BasePrice };

export type QuoteEquipmentWithCharge = Record<string, any> & {
  price: BasePrice;
  attachments: QuoteEquipmentAttachmentWithCharge[];
};

export type QuoteChargeListingEntity = {
  type: QuoteLineItemType;
  charge: Nullable<Charge>;
  equipment: Nullable<QuoteEquipmentWithCharge>;
};

export interface InvoiceListingActions {
  onAddEquipment: VoidFunction;
}

export interface InvoiceListingBucket extends ChargeBucketRef {
  title: string;
  data: QuoteChargeListingEntity[];
}

export type QuoteChargeListingContext = {
  invoiceId: string;
  priceCalc: Nullable<QuoteCalcViewModel>;
  chargeService: ChargeListingService;
  onRemoveRow: (index: number) => void;
  isAnyEquipmentActionInProgress: boolean;
  onEditEquipment: (id: string, rowIndex: number) => void;
  onRemoveEquipment: (id: string, rowIndex: number) => void;
  form: UseFormReturn<ListingForm<QuoteChargeListingEntity, QuoteChargeListingContext>>;
};

export type NetFinanceAmountListingEntity = {
  fees: number;
  taxes: number;
  hardCost: number;
  softCost: number | null;
  netFinanceAmount: number;
};

export type DealerProfitabilityListingEntity = {
  netSellingPrice: number;
  totalDealerCost: number;
  grossProfit: number;
  marginRate: number;
  overUnderAllowanceTotal: number;
};

export enum QuoteChangeLogChangeType {
  INITIAL_CREATION = 'INITIAL_CREATION',
  CHANGE = 'CHANGE',
  CONTACTS_CREATED = 'CONTACTS_CREATED',
  CONTACTS_UPDATED = 'CONTACTS_UPDATED',
  EQUIPMENT_UPDATED = 'EQUIPMENT_UPDATED',
  PROPOSAL_CREATED = 'PROPOSAL_CREATED',
  FINANCE_AND_MENU_CHANGED = 'FINANCE_AND_MENU_CHANGED',
}
